import type { ReactNode } from "react";

import { useT } from "../shared/i18n";
import { Button, Icons, Skeleton } from "../shared/ui";

/**
 * The explicit startup steps from the System States document. There is no
 * grey-or-white screen at any point: the shell with an indicator is always
 * visible, and user data appears only after successful authentication.
 */
export type StartupPhase =
  "host_initializing" | "authenticating" | "resolving_target" | "loading_route";

const PHASE_LABEL = {
  host_initializing: "startup.hostInitializing",
  authenticating: "startup.authenticating",
  resolving_target: "startup.resolvingTarget",
  loading_route: "startup.loadingRoute",
} as const;

function Mark() {
  return (
    <span
      aria-hidden="true"
      className="flex size-18 items-center justify-center rounded-[22px] bg-accent font-sans text-[40px] leading-none font-bold text-on-accent"
    >
      M
    </span>
  );
}

export function StartupScreen({ phase }: { phase: StartupPhase }) {
  const t = useT();
  const label = t(PHASE_LABEL[phase]);
  // The last step shows a skeleton sized like the real shell, so nothing jumps
  // when the Workspace arrives.
  if (phase === "loading_route")
    return (
      <main className="flex min-h-screen flex-col bg-canvas" aria-busy="true">
        <div className="flex flex-col gap-2 px-gutter pt-6 pb-4">
          <Skeleton width={90} height={14} className="rounded-md" />
          <Skeleton width={220} height={34} className="rounded-lg" />
        </div>
        <div className="flex flex-1 flex-col gap-3 px-gutter">
          <Skeleton height={96} />
          <Skeleton height={70} />
          <Skeleton height={70} />
        </div>
        <span className="sr-only">{label}</span>
      </main>
    );
  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas"
      aria-busy="true"
    >
      <Mark />
      <p className="text-callout text-fg-muted">{label}</p>
      <Icons.Spinner size={22} label={label} />
    </main>
  );
}

export function TopBar({
  eyebrow,
  onClose,
  trailing,
}: {
  eyebrow: string;
  onClose?: () => void;
  trailing?: ReactNode;
}) {
  const t = useT();
  return (
    <header
      data-print="hidden"
      className="flex items-center justify-between gap-3 bg-canvas px-gutter pt-4 pb-2"
    >
      <div className="min-w-0">
        <p className="truncate text-caption font-semibold uppercase tracking-wide text-fg-muted">
          {eyebrow}
        </p>
        <strong className="text-title text-fg">{t("app.name")}</strong>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {trailing}
        {onClose && (
          <Button
            variant="tinted"
            size="sm"
            aria-label={t("app.closeAria")}
            onClick={onClose}
          >
            {t("app.close")}
          </Button>
        )}
      </div>
    </header>
  );
}
